// replay-player.ts — the `.wirep` viewer as an embeddable LIBRARY (M3 of tasks/community-view-plan.md).
//
// This is replay-viewer.ts's boot() turned into `startReplayViewer(opts)`: everything page-scoped
// (location.search reads, getElementById against the static template, the auto-boot, drag-drop) moved
// behind options, the viewer DOM built programmatically into `opts.mount` (class-scoped — no id
// collisions with a host page whose canvas is already #screen), and a full teardown path (`stop()`:
// cancel the rAF loop, terminate the prewarm worker, remove the window listeners, release the GL
// context, unmount the DOM) — the page viewer never needed one because navigation killed the page.
//
// Consumers:
//   • the standalone viewer page (replay-viewer.ts — replay.warfareincorporated.com): a thin wrapper
//     that parses location.search / handles drag-drop and mounts on document.body;
//   • the game client (M4): mounts into a full-viewport overlay over the DOM shell and returns to the
//     menu via onExit — no page reload.
//
// Architecture is unchanged from replay-viewer.ts (see its header): the viewer OWNS the sim and the
// clock — stepBlock re-sim, render-only GameRenderer, snapshot checkpoints for seek, a prewarm worker.

import { Sim } from '../../../sim/src/index.ts';
import type { LevelData } from '../../../sim/src/index.ts';
import {
  kfGobMobileUnit,
  kfGobRedraw,
  kfGobVisibleLastFrame,
  kfGobIncludeFindVisible,
  kfGobTransitioningToVisible,
} from '../../../sim/src/gob/gob.ts';
import { kgtScenery } from '../../../sim/src/gob/gobtypes.ts';
import { IniReader } from '../../../sim/src/data/ini.ts';
import { parseCini } from '../../../sim/tools/vendor/cini.cjs';
import { MemPdbReader } from '../io/pdbreader.ts';
import { PackFileReader } from '../io/packfile.ts';
import { LevelDataFromPack } from '../io/levelpack.ts';
import { TexAtlasMgr } from '../gfx/texatlasmgr.ts';
import { CoordMap } from '../gfx/coords.ts';
import type { GlDibBitmap } from '../gfx/gl/glsurface.ts';
import { GlRenderer } from '../gfx/gl/glrenderer.ts';
import { GobDrawContext } from '../render/gobdraw.ts';
import { SpriteManager, AnimSprite } from '../render/sprites.ts';
import { TimerMgr } from '../ui/timer.ts';
import { EventMgr } from '../ui/event.ts';
import { MemorySoundMgr, defaultOptionsState } from '../ui/shellform.ts';
import { SimUIForm as SimUIFormInput } from '../ui/fingerhandler.ts';
import { GetSideColor } from '../gfx/palette.ts';
import { GameRenderer } from '../ui/gamerenderer.ts';
import { kcmsUpdate } from '../../../sim/src/math/coords.ts'; // 80 game-ms per sim update
import { resetProjectileInterp } from '../render/projectile-interp.ts';
import { parseReplay } from '../../../workers/multiplayer/src/replay.ts';
import { decodePng } from './png.ts';
import { WebBuffer } from './buffer-shim.ts';
import { fogHost } from './fog-host.ts';
import { initReplayAudio, fetchSfxBytes } from './replay-audio.ts';
import { mountMissionPack } from './replay-pack.ts';
import { initReplayBrowse } from './replay-browse.ts';
import { escapeHtml, replayDerived } from './replay-common.ts';
import {
  makeEventCollector,
  eventKey,
  type ReplayEvent,
  type EconomySample,
} from './replay-events.ts';
import templatesJson from '../../../sim/data/GobTemplates.ini.json';
import anirJson from '../../../sim/data/anir.json';
import { anirJsonFromPack } from '../gfx/anirpack.ts';

const W = 1024,
  H = 768;
const BLACK = { r: 0, g: 0, b: 0 }; // wipe reused draw surfaces on a rebind (fog correctness)
// the playback-speed menu — a GAME-TIME multiplier (1× = the sim's real 12.5 updates/s,
// frame-rate independent; fractions = slow motion, where the projectile-glide interpolation
// keeps shots moving smoothly BETWEEN sim updates instead of hopping at the update rate)
const SPEEDS = [1 / 16, 1 / 8, 1 / 4, 1 / 2, 1, 2, 4, 8, 16, 32, 64, 128, 256];
const speedLabel = (s: number): string => (s < 1 ? `1/${Math.round(1 / s)}×` : `${s}×`);
const kTicksPerUpdate = kcmsUpdate / 10; // host ticks (1/100s) per sim update = 8

/** Where the recording comes from: an R2 archive key (fetched via `${replayBase}/r/<key>`),
 *  a full URL, or raw bytes (the standalone page's drag-drop / sessionStorage stash). */
export type ReplaySource = { key: string } | { url: string } | { bytes: Uint8Array };

export interface ReplayViewerOpts {
  /** the container the viewer builds its DOM into. The caller sizes/positions it (the standalone
   *  page passes document.body; the game passes a fixed inset-0 overlay). */
  mount: HTMLElement;
  source: ReplaySource;
  /** art pack: '832' (default) | '824' | '2432'. */
  art?: string;
  /** start showing a side's fog of war (default: neutral reveal-all spectator). */
  fog?: boolean;
  /** the fog side (default: the roster's first side). */
  side?: number;
  /** game-speed multiplier (default 4; 1 = real time, fractions = slow motion). */
  speed?: number;
  /** force the CPU renderer (GL is the default). */
  forceCpu?: boolean;
  /** origin for `{key}` sources — `${replayBase}/r/<key>` ('' = same-origin, the standalone page). */
  replayBase?: string;
  /** the add-on mission-pack URL for a content key `<id8hex>-<md5>` (default: the standalone
   *  page's same-origin `/wi-pack/<key>` proxy; the game passes its own pack base). */
  packUrlFor?: (key: string) => string;
  /** the art pack URL (default: same-origin `/<packName>` — both the viewer page and the game
   *  origin stage the atlas packs at the root). */
  artPackUrl?: string;
  /** the prewarm Worker bundle URL (default '/prewarm-worker.js'); null = in-thread warmer only. */
  prewarmWorkerUrl?: string | null;
  /** the ☰ archive browse panel: apiBase = the /api/replays origin ('' = same-origin), onPick =
   *  what picking a recording does (default: reload with ?replay=<key> — the standalone page).
   *  false = hide the browse button. */
  browse?: { apiBase?: string; currentKey?: string | null; onPick?: (key: string) => void } | false;
  /** present ⇒ embedded mode: a ✕ close button in the transport bar + Escape exits. */
  onExit?: () => void;
}

/** The live-viewer control surface (also published as window.wiReplay for the headless drivers). */
export interface ReplayHandle {
  state(): Record<string, unknown>;
  pause(): void;
  play(): void;
  advance(n: number): number;
  /** advance the paused game clock by ms (default one update = 80). Sub-update amounts move
   *  the projectile glide between sim steps; whole updates step the sim. Returns the clock
   *  target in game-ms. */
  stepClock(ms?: number): number;
  seek(u: number): number;
  zoom(z: number): number;
  fog(s: number | null): number | null;
  /** tear the viewer down: stop the clock/worker/listeners, release GL, unmount the DOM. */
  stop(): void;
}

// ── the viewer DOM (was tools/replay-page.mjs markup + id-keyed CSS) ────────────────
const STYLE_ID = 'wi-replay-player-style';
function ensurePlayerStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent =
    '.wirv-econ{scrollbar-width:none}.wirv-econ::-webkit-scrollbar{display:none}' +
    '.wirv .fogbtn{display:inline-block;width:9px;height:9px;margin-right:6px;vertical-align:middle;cursor:pointer}' +
    '.wirv-tbtn:hover{background:#1a4d2e}' +
    '.wirv-play-overlay:hover .wirv-play-btn{background:#1a4d2e;transform:scale(1.06)}';
  (document.head ?? document.documentElement).appendChild(style);
}

interface ViewerDom {
  econ: HTMLElement;
  map: HTMLElement;
  screenwrap: HTMLElement;
  screen: HTMLCanvasElement;
  minimap: HTMLCanvasElement;
  graph: HTMLElement;
  graphCanvas: HTMLCanvasElement;
  status: HTMLElement;
  statusText: HTMLElement;
  syncBadge: HTMLElement;
  playpause: HTMLButtonElement;
  speedSel: HTMLSelectElement;
  sfx: HTMLButtonElement;
  browse: HTMLButtonElement;
  chart: HTMLButtonElement;
  close: HTMLButtonElement | null;
}

function buildViewerDom(
  mount: HTMLElement,
  withClose: boolean,
): ViewerDom & { scrub: HTMLElement } {
  ensurePlayerStyles();
  mount.classList.add('wirv');
  const ms = mount.style;
  ms.display = 'flex';
  ms.flexDirection = 'column';
  ms.background = '#000';
  ms.color = '#8f8';
  ms.font = '12px ui-monospace,monospace';
  ms.overflow = 'hidden';
  ms.userSelect = 'none';
  (ms as unknown as Record<string, string>).webkitUserSelect = 'none';
  ms.touchAction = 'none';
  ms.overscrollBehavior = 'none';
  if (mount !== document.body && getComputedStyle(mount).position === 'static') {
    ms.position = 'relative'; // the minimap anchors absolute against the mount
  }
  if (mount === document.body) ms.height = '100%';

  const div = (css: string): HTMLElement => {
    const e = document.createElement('div');
    e.style.cssText = css;
    return e;
  };
  // the per-side economy strip is the FIRST flex row — flush with the mount top, i.e. under the
  // iPhone notch / status bar. Pad the top by the safe-area inset so the player info drops to just
  // below the status bar (the map below still full-bleeds up behind it). left/right insets keep it
  // clear of the notch corners in landscape.
  const econ = div(
    'flex:none;text-align:center;padding:calc(4px + env(safe-area-inset-top,0px)) env(safe-area-inset-right,0px) 4px env(safe-area-inset-left,0px);min-height:17px;line-height:17px;white-space:nowrap;overflow-x:auto;overflow-y:hidden;touch-action:pan-x',
  );
  econ.classList.add('wirv-econ');
  const map = div(
    'flex:1 1 auto;min-height:0;display:flex;align-items:center;justify-content:center;overflow:hidden;position:relative',
  );
  const screenwrap = div('overflow:hidden;line-height:0');
  const screen = document.createElement('canvas');
  screen.width = W;
  screen.height = H;
  screen.style.cssText = 'image-rendering:pixelated;touch-action:none;display:block';
  screenwrap.appendChild(screen);
  const minimap = document.createElement('canvas');
  minimap.style.cssText =
    'position:absolute;right:10px;bottom:calc(env(safe-area-inset-bottom,0px) + 36px);z-index:5;border:1px solid #0a0;background:rgba(0,0,0,.78);cursor:crosshair;image-rendering:pixelated;touch-action:none;transition:bottom .16s ease';
  map.append(screenwrap);
  const status = div(
    'flex:none;display:flex;align-items:center;gap:8px;padding:2px 10px;white-space:nowrap;overflow:hidden',
  );
  const statusText = document.createElement('span');
  statusText.style.cssText = 'overflow:hidden;text-overflow:ellipsis;min-width:0';
  statusText.textContent = 'loading…';
  // sync-error indicator: hidden until the viewer detects a desync or a failed checkpoint
  // restore. A failed restore is exactly what silently degraded seeking to a full re-sim from
  // seed (the Overmind snapshot bug) — surfacing it makes the next such regression obvious.
  const syncBadge = document.createElement('span');
  syncBadge.className = 'wirv-syncwarn'; // headless drivers' hook
  syncBadge.style.cssText =
    'display:none;flex:none;padding:0 6px;border-radius:3px;background:#5a1a1a;color:#ffb3b3;border:1px solid #a33;font:11px ui-monospace,monospace;cursor:help';
  status.append(statusText, syncBadge);
  const transport = div(
    'flex:none;display:flex;align-items:stretch;height:22px;margin:0 10px max(8px,env(safe-area-inset-bottom,0px));border-radius:4px;overflow:hidden;background:#0a1410',
  );
  const tbtnCss =
    'flex:none;background:#123;color:#8f8;border:none;border-right:1px solid #042;cursor:pointer;font:12px ui-monospace,monospace;padding:0;touch-action:manipulation';
  const playpause = document.createElement('button');
  playpause.style.cssText = `${tbtnCss};width:34px`;
  playpause.classList.add('wirv-tbtn', 'wirv-playpause'); // the class is the headless drivers' hook
  playpause.title = 'play / pause (Space)';
  playpause.textContent = '▶';
  const speedSel = document.createElement('select');
  speedSel.style.cssText =
    'flex:none;background:#123;color:#8f8;border:none;border-right:1px solid #042;cursor:pointer;font:11px ui-monospace,monospace;outline:none;padding:0 4px;touch-action:manipulation';
  speedSel.title = 'playback speed';
  const sfx = document.createElement('button');
  sfx.style.cssText = `${tbtnCss};width:30px`;
  sfx.classList.add('wirv-tbtn');
  sfx.title = 'sound on / off';
  sfx.textContent = '🔊';
  const browse = document.createElement('button');
  browse.style.cssText = `${tbtnCss};width:30px`;
  browse.classList.add('wirv-tbtn');
  browse.title = 'browse recordings';
  browse.textContent = '☰';
  const chart = document.createElement('button');
  chart.style.cssText = `${tbtnCss};width:30px`;
  chart.classList.add('wirv-tbtn', 'wirv-chart');
  chart.title = 'economy graph — credits ($) + unit counts over time';
  chart.textContent = '📈';
  chart.setAttribute('aria-expanded', 'false');
  const scrub = div('flex:1 1 auto;height:100%;background:#123;cursor:pointer;touch-action:none');
  transport.append(playpause, speedSel, sfx, browse, chart, scrub);
  // the expandable economy graph — a flex:none panel above the transport, height 0 until the
  // 📈 toggle opens it (the map area flexes down to make room). Its own canvas paints the
  // per-side credits-over-time series (all sides in one graph, x = the same timeline).
  const graph = div(
    'flex:none;height:0;overflow:hidden;transition:height .16s ease;margin:0 10px;position:relative;background:#0a1410;border-radius:4px',
  );
  graph.classList.add('wirv-graph');
  const graphCanvas = document.createElement('canvas');
  graphCanvas.style.cssText =
    'display:block;width:100%;height:100%;touch-action:none;cursor:pointer';
  graph.appendChild(graphCanvas);
  let close: HTMLButtonElement | null = null;
  if (withClose) {
    // embedded mode: a big top-right floating ✕ (safe-area aware), not a transport-bar cell —
    // the exit control should read like a screen close, and be an easy touch target.
    close = document.createElement('button');
    close.style.cssText =
      'position:absolute;top:max(10px,env(safe-area-inset-top,0px));right:max(10px,env(safe-area-inset-right,0px));z-index:6;' +
      'width:44px;height:44px;background:rgba(10,20,16,.85);color:#8f8;border:1px solid #0a0;border-radius:4px;' +
      'cursor:pointer;font:20px ui-monospace,monospace;line-height:1;padding:0;touch-action:manipulation';
    close.classList.add('wirv-tbtn');
    close.title = 'close (Esc)';
    close.setAttribute('aria-label', 'Close replay');
    close.textContent = '✕';
  }
  mount.replaceChildren(econ, map, graph, status, transport);
  mount.append(minimap); // absolute against the mount (was position:fixed on the page)
  if (close !== null) mount.append(close); // absolute top-right against the mount
  return {
    econ,
    map,
    screenwrap,
    screen,
    minimap,
    graph,
    graphCanvas,
    status,
    statusText,
    syncBadge,
    playpause,
    speedSel,
    sfx,
    browse,
    chart,
    close,
    scrub,
  };
}

async function resolveSource(source: ReplaySource, replayBase: string): Promise<Uint8Array> {
  if ('bytes' in source) return source.bytes;
  const url = 'key' in source ? `${replayBase}/r/${source.key}` : source.url;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`recording ${r.status}`);
  return new Uint8Array(await r.arrayBuffer());
}

// The SIM's tile size, which is NOT the art's. gcxTile feeds GetSpecialPoint32's 4/3
// scaling (sim-quirk A12), and live play never sets it (playlevel.ts) — so every recorded
// game ran the sim at this default while the client rendered whatever art its window
// selected. Deriving it from the viewer's ?art would re-simulate a DIFFERENT game than the
// one recorded: measured 44 of 403 blocks matching at ?art=824, with nothing warning.
const SIM_TILE = 32;

export async function startReplayViewer(opts: ReplayViewerOpts): Promise<ReplayHandle> {
  const ART = opts.art ?? '832';
  const START_FOGGED = opts.fog === true;
  const FORCE_CPU = opts.forceCpu === true;
  // snap the requested speed to a menu member — select.value can't hold a non-option value (it
  // reads back '' and the per-frame guard would re-assign it forever with a blank dropdown)
  const speedArg = Math.max(1 / 16, opts.speed ?? 4);
  let speed = SPEEDS.reduce((a, b) => (Math.abs(b - speedArg) < Math.abs(a - speedArg) ? b : a));
  const replayBase = opts.replayBase ?? '';
  const packUrlFor = opts.packUrlFor ?? ((key: string) => `/wi-pack/${key}`);
  const prewarmWorkerUrl =
    opts.prewarmWorkerUrl === undefined ? '/prewarm-worker.js' : opts.prewarmWorkerUrl;

  const dom = buildViewerDom(opts.mount, opts.onExit !== undefined);
  const canvas = dom.screen;
  const minimap = dom.minimap;
  const mmCtx = minimap.getContext('2d')!;
  const setStatus = (s: string): void => {
    dom.statusText.textContent = s;
  };

  // ── teardown registry: everything stop() must unwind ──────────────────────────
  let stopped = false;
  let rafId = 0;
  const winListeners: Array<[string, (e: never) => void]> = [];
  const onWin = <K extends keyof WindowEventMap>(
    type: K,
    fn: (e: WindowEventMap[K]) => void,
  ): void => {
    window.addEventListener(type, fn as never);
    winListeners.push([type, fn as never]);
  };

  let glr: GlRenderer | null = null;
  if (!FORCE_CPU) {
    try {
      glr = GlRenderer.create(canvas);
    } catch (e) {
      console.warn('webgl2 unavailable, CPU fallback:', e);
    }
  }
  const rendererName = glr !== null ? 'webgl2' : 'cpu';
  const glFactory =
    glr === null ? undefined : (cx: number, cy: number) => glr!.createSurface(cx, cy);
  const ctx2d = glr === null ? canvas.getContext('2d')! : null;

  // --- fetch the recording + the art pack in parallel
  setStatus('loading…');
  const tile = ART === '824' ? 24 : 32;
  const packName =
    ART === '2432' ? 'htdata2432.pdb' : `htdata${ART === '824' ? '824' : '832'}atlas.pdb`;
  const [wirepBytes, packBytes, sfxBytes] = await Promise.all([
    resolveSource(opts.source, replayBase),
    fetch(opts.artPackUrl ?? `/${packName}`)
      .then((r) => {
        if (!r.ok) throw new Error(`pack ${r.status}`);
        return r.arrayBuffer();
      })
      .then((b) => new Uint8Array(b)),
    fetchSfxBytes(),
  ]);

  // --- art stack (mirrors gl-oracle.ts)
  const pdb = new MemPdbReader();
  if (!pdb.Open(packBytes)) throw new Error('pdb open failed');
  const pakr = new PackFileReader();
  if (!pakr.Push(null, packName, pdb)) throw new Error('pack push failed');
  const tam = new TexAtlasMgr();
  if (!tam.Init(pakr, decodePng)) throw new Error('atlas init failed');
  const coords = new CoordMap(tile);
  const ini = (n: string): IniReader =>
    new IniReader(parseCini(WebBuffer.from(pakr.MapFile(n)!)) as never);
  const gameIni = ini('game.ini');
  const templates = (templatesJson as { sections: unknown }).sections as never;
  const anir = ART === '2432' ? anirJsonFromPack(pakr) : (anirJson as never);

  // --- the recording → roster + level (the level comes from the pack)
  const rep = parseReplay(wirepBytes);
  const lvl = rep.header.level;
  await mountMissionPack(pakr, rep.header, setStatus, packUrlFor); // add-on DLC levels (shared module)
  // --- audio (shared module): the sim's PlaySfx host seam → the game's SoundMgr. Gated to
  // LIVE forward stepping (seeks/prewarm stay silent); neutral spectating plays the
  // battlefield palette only, a toggled fog side hears that player's full stream.
  const audio = initReplayAudio(sfxBytes, dom.sfx);
  let sfxGate = false;
  const viewerHost = {
    ...fogHost,
    PlaySfx: (sfx: number | string): void => {
      if (sfxGate) audio.play(sfx, fogSide !== null);
    },
  };

  const levelData = LevelDataFromPack(pakr, lvl) as LevelData;
  const sides = [...new Set(rep.header.roster.map((r) => r.side))];
  const localSide = opts.side ?? rep.header.roster[0]?.side ?? sides[0];
  // The roster with `local` pointed at one side — that side's units reveal the fog (gpplrLocal);
  // the trio is the determinism contract shared with the 3D viewer (replay-common.ts).
  const { rosterFor, lastBlock, hashByBlock } = replayDerived(rep);
  let fogSide: number | null = START_FOGGED ? localSide : null; // which side's fog to render (null = all)

  // --- a render-only GameRenderer over a sim (see replay-viewer.ts for the design notes).
  function buildRenderer(): { renderer: GameRenderer; sui: SimUIFormInput } {
    // closures read the outer mutable `sim`
    const gctx = new GobDrawContext(pakr, tam, coords);
    const sizPlayfield = { cx: 0, cy: 0 };
    const sprm = new SpriteManager();
    const hilightSprites = new Map<unknown, AnimSprite>();
    // the game clock, like getTickCount below — so any HUD presentation timer that ever
    // runs here (AlertState's 4s dismiss etc.) tracks playback instead of sitting at t=0
    const gtimm = new TimerMgr(() => gameTicksNow());
    const evm = new EventMgr(
      {
        // never pumped (no input/events)
        hostGetEvent: () => null,
        clockMs: () => 0,
        cookEvent: () => {},
        checkSetRedrawDirty: () => {},
        filterSleepTicks: (ct) => ct,
      },
      gtimm,
    );
    const sndm = new MemorySoundMgr();
    const options = defaultOptionsState();
    const sui = new SimUIFormInput({
      ctx: sim.ctx,
      sim: sim.sim,
      gctx,
      evm,
      GetTickCount: () => gameTicksNow(), // the game-time clock (was pinned to 0)
      GetPlayfieldSize: () => sizPlayfield,
      host: {
        PopupMenu: () => {},
        GetPlaceForm: () => null,
        CreateUnitTitle: () => null,
        DestroyUnitTitle: () => {},
        PlaySfx: () => {},
        CreateSelectionSprite: () => sprm.CreateSelectionSprite(),
        CreateHilightSprite: (punt: unknown) => {
          if ((punt as { m_fReplicating?: boolean }).m_fReplicating !== undefined) return;
          if (!hilightSprites.has(punt)) hilightSprites.set(punt, sprm.CreateAnimSprite(gctx));
        },
        DestroyHilightSprite: (punt: unknown) => {
          hilightSprites.get(punt)?.Dispose();
          hilightSprites.delete(punt);
        },
      } as never,
    });
    const renderer = new GameRenderer({
      // no scopedInvalidation: the render-only sim host has none of the GameSession
      // damage seams, so this renderer keeps the full-repaint default.
      pakr,
      tam,
      coords,
      gameIni,
      templates,
      tmapName: lvl.replace(/\.lvl$/, '.tmap'),
      screen: { cx: W, cy: H },
      createSurface: glFactory,
      gctx,
      sizPlayfield,
      sprm,
      hilightSprites,
      sui,
      gtimm,
      evm,
      sndm,
      options,
      getUpdateCount: () => sim.sim.GetUpdateCount(),
      getGobm: () => sim.ctx.gobm,
      getPvar: (n) => sim.ctx.pvars.Get(n),
      getAnimData: (n) => sim.ctx.animDataFor(n),
      // the game-time clock (playhead ticks). Was pinned to 0, which left the projectile
      // glide (render/projectile-interp.ts) DORMANT in the viewer — its interp fraction
      // never advanced, so shots hopped at the 12.5Hz update rate. On the playhead clock
      // the glide follows playback exactly: slow motion stays smooth, a paused clock
      // freezes shots MID-GLIDE, and clock steps/scrubs move them sub-update.
      getTickCount: () => gameTicksNow(),
      getHandicap: () => options.wfHandicap,
      getMouseMode: () => options.fMouseMode,
    });
    renderer.prime(); // arm the playfield first-paint (no OnTimer)
    // Spectator: hide in-production mobile units parked at the (0,0) staging tile (fog would).
    type VGob = { GetFlags(): number; m_wx: number; m_wy: number };
    const staging = (g: VGob): boolean =>
      (g.GetFlags() & kfGobMobileUnit) !== 0 && g.m_wx < 256 && g.m_wy < 256;
    const fvg = (sui as unknown as { FindVisibleGobs(): VGob[] }).FindVisibleGobs.bind(sui);
    (sui as unknown as { FindVisibleGobs(): VGob[] }).FindVisibleGobs = () =>
      fvg().filter((g) => !staging(g));
    return { renderer, sui };
  }

  // --- viewer state: one sim driven by stepBlock, painted by one renderer
  let sim!: Sim, renderer!: GameRenderer, sui!: SimUIFormInput;
  let rendererReady = false; // renderer+sui are built ONCE, then REBOUND each seek
  let cursor = 0; // index of the next unplayed block
  let hashState = { matched: 0, mism: 0 };
  // seeks that fell back to a from-seed rebuild because a checkpoint restore disagreed with our
  // own re-sim — i.e. a snapshot-completeness gap (the "slow seek" signal). Distinct from a
  // recording desync (hashState.mism), which is the recording vs our re-sim.
  let checkpointRestoreFails = 0;
  let viewWx = 0,
    viewWy = 0,
    viewInit = false; // the camera (world top-left); the user pans it
  let zoom = 1,
    maxZoom = 1; // 1 = native tiles; >1 = zoomed out (bigger surface)
  // Start PAUSED behind the play-button overlay (see the overlay below). Browsers keep the
  // AudioContext suspended until a user gesture, so an autoplaying replay would drop its sfx
  // silently; gating the start on a click both unlocks audio and lets the prewarm prefill run
  // while the viewer waits. The overlay's click flips this to true.
  let playing = false;
  // in-memory snapshots every ~K_CP updates; a seek restores the nearest one ≤ target and
  // fast-forwards the remainder (≤ K_CP updates). That fast-forward re-sim is the whole cost
  // of a seek (restore is ~3ms; 500 updates is ~60ms on a busy game — measured), so K_CP is the
  // seek-latency knob: halving it halves the worst-case fast-forward at ~2× the snapshot memory
  // (each snap is only ~10–50KB). 250 keeps a big game's checkpoint set to a few MB.
  const K_CP = 250;
  const checkpoints: { blockPoint: number; snap: Uint8Array }[] = []; // sorted ascending by blockPoint
  const cpSet = new Set<number>(); // blockPoints already cached (dedup: lazy path vs prewarm)
  let nextCp = K_CP; // next update at which the LAZY path snapshots (high-water)
  function addCheckpoint(blockPoint: number, snap: Uint8Array): void {
    if (cpSet.has(blockPoint)) return;
    cpSet.add(blockPoint);
    let i = checkpoints.length; // insert in sorted position (nearest-≤-t find needs order)
    while (i > 0 && checkpoints[i - 1].blockPoint > blockPoint) i--;
    checkpoints.splice(i, 0, { blockPoint, snap });
    recomputeWarmFrontier();
  }
  function dropCheckpoint(cp: { blockPoint: number; snap: Uint8Array }): void {
    const i = checkpoints.indexOf(cp);
    if (i >= 0) checkpoints.splice(i, 1);
    cpSet.delete(cp.blockPoint);
    recomputeWarmFrontier();
  }
  // the CONTIGUOUS warmed span from the start (the scrubber's warm fill) — see replay-viewer.ts.
  let warmFrontier = 0;
  function recomputeWarmFrontier(): void {
    let f = 0;
    for (const cp of checkpoints) {
      if (cp.blockPoint <= f + K_CP * 1.5) f = cp.blockPoint;
      else break;
    }
    warmFrontier = f;
  }
  // `playhead` doubles as the GAME-TIME clock (fractional updates; host ticks = playhead×8):
  // ANIMATION time is the master timeline and sim time is quantized onto it — one update per
  // kcmsUpdate game-ms (the same model as the 3D viewer). It feeds the renderer's
  // HostGetTickCount, which drives the sim-decoupled presentation (the projectile glide +
  // after-image in render/projectile-interp.ts — dormant here before, since the viewer pinned
  // that clock to 0). Pausing stops the clock (pause IS freeze — nothing keeps animating);
  // . / > / → / the scrubber advance it, the sim following whenever a whole update
  // accumulates, and the sub-update remainder positions shots BETWEEN their sim steps.
  let playhead = -1; // monotonic playback clock (fractional UPDATES), drives play + ticks
  let stepGameMs = 0; // queued clock steps (. / > / →), consumed by the next frame
  let lastPlayhead = -2; // present() when the clock moved — the glide only shows if painted
  // FRACTIONAL ticks (continuous glide even at 1/16×). The other tick consumers are timing
  // stats/gesture thresholds that only difference the value — fractions are harmless there.
  const gameTicksNow = (): number => Math.max(0, playhead) * kTicksPerUpdate;
  let lastReveal = -1,
    mmTick = 0; // RevealAll only when the sim moved; minimap throttle tick
  let pendingSeek: number | null = null; // scrubber target → one synchronous seekTo per frame (coalesced)

  // OUR OWN re-sim's hash per block — the ground truth checkpoint restores are validated against.
  // Populated by every trusted (from-seed lineage) pass: buildAt sims and the background prewarm
  // (both re-sim the same deterministic stream, so their hashes are interchangeable). The RECORDING's
  // hashes (hashState) can legitimately mismatch ours — a desynced game's .wirep is exactly what this
  // viewer gets pointed at — so they must never be the drop-a-checkpoint signal (that conflation made
  // every seek past a divergence drop a good checkpoint and re-sim from seed). Fog changes don't clear
  // this: the sync hash covers sim state only (fog/reveal is presentation, unhashed).
  const simHashByBlock = new Map<number, number>();
  let restoreMism = 0; // blocks where the CURRENT (restored) sim contradicts the ground truth
  function attachHash(s: Sim, trusted: boolean): void {
    // count per-block hashes vs the recording (display), and vs our own re-sim (restore validation)
    let matched = 0,
      mism = 0;
    s.onBlockHash = (block, hash) => {
      const h = hash >>> 0;
      const own = simHashByBlock.get(block);
      if (own !== undefined) {
        if (h !== own) restoreMism++; // diverged from our own from-seed re-sim → bad snapshot
      } else if (trusted) {
        simHashByBlock.set(block, h);
      } else if (hashByBlock.get(block) !== undefined && h !== hashByBlock.get(block)) {
        restoreMism++; // no ground truth yet → recording fallback (a false positive here costs one
        // rebuild, which then records the truth up to the target, so it can't repeat)
      }
      const want = hashByBlock.get(block);
      if (want === undefined) return;
      if (h === want) matched++;
      else mism++;
      hashState = { matched, mism };
    };
    hashState = { matched: 0, mism: 0 };
    restoreMism = 0;
  }

  // attach the (possibly-restored) sim's renderer + zoom + camera (shared by buildAt + restoreFrom).
  function attachRenderer(): void {
    // force every gob to redraw + clear stale visibility flags (see replay-viewer.ts for why).
    const RENDER_VIS =
      (kfGobVisibleLastFrame | kfGobIncludeFindVisible | kfGobTransitioningToVisible) >>> 0;
    for (let g = sim.ctx.gobm.GetFirstGob(); g !== null; g = sim.ctx.gobm.GetNextGob(g)) {
      g.SetFlags((((g.GetFlags() & ~RENDER_VIS) >>> 0) | kfGobRedraw) >>> 0);
    }
    if (!rendererReady) {
      // First attach: build the renderer + sui ONCE; later seeks REBIND (cheap path).
      ({ renderer, sui } = buildRenderer());
      if (zoom !== 1) {
        const s = surfaceFor(zoom);
        renderer.resize(s.cx, s.cy);
      } // keep the zoom level
      if (viewInit) sui.SetViewPos(viewWx, viewWy); // keep the camera
      rendererReady = true;
    } else {
      // Rebind: re-point the existing sui at the new sim; wipe reused surfaces (fog correctness).
      sui.Rebind(sim.ctx, sim.sim);
      renderer.pfrmSimUI.RebindView(); // the HUD form caches its OWN fog/terrain view → rebind it too
      for (const spr of renderer.hilightSprites.values()) spr.Dispose();
      renderer.hilightSprites.clear();
      renderer.tmap.m_pbmDraw?.Clear(BLACK);
      renderer.back.Clear(BLACK);
      sui.SetViewPos(
        viewInit ? viewWx : sim.sim.m_wxView,
        viewInit ? viewWy : sim.sim.m_wyView,
        true,
      );
      renderer.mfrmm.InvalidateRect(null);
    }
    lastReveal = -1; // new sim → re-reveal fog on the next present()
  }

  // buildAt(targetUpdate): fresh sim from the SEED → fast-forward via raw stepBlock.
  function buildAt(targetUpdate: number): void {
    sim = Sim.create({
      levelName: lvl,
      levelData,
      templates,
      anir,
      seed: rep.header.seed,
      simVersion: rep.header.simVersion,
      roster: rosterFor(fogSide ?? localSide),
      multiplayer: true,
      gcxTile: SIM_TILE,
      host: viewerHost,
    });
    attachHash(sim, true); // from seed → its hashes are ground truth
    cursor = 0;
    stepTo(targetUpdate); // tick-step to the EXACT update (not the block ≤ it)
    attachRenderer();
  }

  // restoreFrom(cp): bit-exact snapshot restore — the fast path for backward seek.
  function restoreFrom(cp: { blockPoint: number; snap: Uint8Array }): void {
    sim = Sim.restore({
      snapshot: cp.snap,
      levelDataFor: () => levelData,
      templates,
      anir,
      multiplayer: true,
      gcxTile: SIM_TILE,
      host: viewerHost,
    });
    attachHash(sim, false); // restored → validate against ground truth, don't write it
    cursor = rep.blocks.findIndex((b) => b.block > cp.blockPoint);
    if (cursor < 0) cursor = rep.blocks.length;
    attachRenderer();
  }

  // step the recorded block stream up to a target UPDATE count (bit-exact) — see replay-viewer.ts.
  function stepTo(target: number): void {
    while (sim.sim.GetUpdateCount() < target) {
      if (sim.stepOnce()) continue; // advanced one tick toward the block point
      if (sim.sim.GetUpdateCount() >= nextCp) {
        const bp = sim.resumeBlockPoint();
        if (!cpSet.has(bp)) addCheckpoint(bp, sim.snapshot());
        nextCp = sim.sim.GetUpdateCount() + K_CP;
      }
      if (cursor >= rep.blocks.length) break; // no more blocks → end of the recording
      sim.queueBlock(rep.blocks[cursor].block, rep.blocks[cursor].cmds);
      cursor++;
    }
  }
  function advanceUpdates(n: number): void {
    stepTo(sim.sim.GetUpdateCount() + n);
  }

  // --- camera: the user pans (drag the canvas). Auto-center only on load + the C key.
  function setView(wx: number, wy: number): void {
    sui.SetViewPos(wx, wy); // clamps to [0, map-playfield] (fingerhandler)
    viewWx = sim.sim.m_wxView;
    viewWy = sim.sim.m_wyView;
    viewInit = true;
  }
  const mapEl = dom.map;
  // the render surface (a viewport of the map at native tile resolution) — see replay-viewer.ts.
  function surfaceFor(z: number): { cx: number; cy: number } {
    const dims = sim.ctx.gobm.GetMapSize(); // { ctx, cty } in tiles
    const mapW = dims.ctx * tile,
      mapH = dims.cty * tile;
    const mw = mapEl.clientWidth,
      mh = mapEl.clientHeight;
    const ar = mw > 0 && mh > 0 ? mw / mh : W / Math.max(1, H - renderer.cyInput); // #map aspect (fallback: native)
    const coverPfH = Math.min(mapH, Math.round(mapW / ar));
    const coverZ = (coverPfH + renderer.cyInput) / H;
    let pfH: number, cx: number;
    if (z <= coverZ) {
      // fill the map area (window aspect), capped at the map
      pfH = Math.min(Math.round(H * z) - renderer.cyInput, mapH);
      cx = Math.min(Math.round(pfH * ar), mapW);
    } else {
      // past cover: blend the cropped axis out to the full map
      const coverCx = Math.min(Math.round(coverPfH * ar), mapW);
      const t = Math.min(1, (z - coverZ) / Math.max(1e-6, maxZoom - coverZ));
      pfH = Math.round(coverPfH + t * (mapH - coverPfH));
      cx = Math.round(coverCx + t * (mapW - coverCx));
    }
    return { cx: Math.min(cx, mapW), cy: Math.min(pfH, mapH) + renderer.cyInput };
  }
  function computeMaxZoom(): number {
    // the zoom at which the WHOLE map fits (contain)
    const dims = sim.ctx.gobm.GetMapSize();
    const mapW = dims.ctx * tile,
      mapH = dims.cty * tile;
    const mw = mapEl.clientWidth,
      mh = mapEl.clientHeight;
    const ar = mw > 0 && mh > 0 ? mw / mh : W / Math.max(1, H - renderer.cyInput);
    const fitPfH = Math.max(mapH, Math.round(mapW / ar)); // playfield height that contains the full map
    return Math.max(1, (fitPfH + renderer.cyInput) / H);
  }
  function recenter(): void {
    const dump = sim.dumpGobs(); // [gid, side, type, wx, wy]
    const watched = fogSide === null ? dump : dump.filter((g) => g[1] === fogSide);
    const pool = watched.length ? watched : dump;
    let cx = 0,
      cy = 0;
    if (pool.length) {
      for (const g of pool) {
        cx += g[3];
        cy += g[4];
      }
      cx = Math.round(cx / pool.length);
      cy = Math.round(cy / pool.length);
    }
    setView(
      cx - coords.WcFromPc(Math.floor(renderer.sizPlayfield.cx / 2)),
      cy - coords.WcFromPc(Math.floor(renderer.sizPlayfield.cy / 2)),
    );
  }

  // --- per-side economy panel (DOM, neutral spectator HUD) — see replay-viewer.ts.
  const sideCss = (side: number): string => {
    const c = GetSideColor(side);
    return `rgb(${c.r},${c.g},${c.b})`;
  };
  const econ = dom.econ;
  let lastEcon = ''; // only rewrite the econ innerHTML when the text changes
  function updateEcon(gobs: number[][]): void {
    const plrm = sim.ctx.plrm as unknown as {
      GetPlayer(s: number): {
        GetCredits(): number;
        GetPowerSupply(): number;
        GetPowerDemand(): number;
        GetName(): string;
        GetSide(): number;
      } | null;
    };
    const onMap = new Set<number>();
    for (const g of gobs) onMap.add(g[1]); // g = [gid, side, type, wx, wy]
    const chips: string[] = [];
    for (let side = 1; side <= 16; side++) {
      if (!onMap.has(side)) continue; // skip filler AI / eliminated (not on the map)
      const p = plrm.GetPlayer(side);
      if (p === null) continue;
      // names come from the .wirep header (arbitrary attacker-authorable strings) → escape
      // before they touch innerHTML (slice first so an entity is never cut in half)
      const raw = p.GetName() || 'side ' + p.GetSide();
      const nm0 = escapeHtml(raw);
      const nm = escapeHtml(raw.length > 8 ? raw.slice(0, 8) : raw.padEnd(8));
      const cr = String(Math.min(99999, p.GetCredits())).padStart(5);
      const sp = String(Math.min(999, p.GetPowerSupply())).padStart(3);
      const dm = String(Math.min(999, p.GetPowerDemand())).padStart(3);
      const warn = p.GetPowerDemand() > p.GetPowerSupply() ? ' !' : '  ';
      const col = sideCss(p.GetSide());
      const on = fogSide === p.GetSide();
      const btn = `<span class="fogbtn" data-fog="${p.GetSide()}" title="show ${nm0}'s fog of war (click again = off)" style="border:1px solid ${col};background:${on ? col : 'transparent'}"></span>`;
      chips.push(
        `<span style="display:inline-block;white-space:pre;margin:0 10px;color:${col}">${btn}${nm} $${cr}  ${sp}/${dm}${warn}</span>`,
      );
    }
    const html = chips.join('');
    if (html !== lastEcon) {
      econ.innerHTML = html;
      lastEcon = html;
    } // skip the DOM write when unchanged
  }

  // zoom = render into a BIGGER back surface and scale to the canvas — see replay-viewer.ts.
  const screenwrap = dom.screenwrap;
  function fitCanvas(): void {
    const sz = renderer.back.GetSize();
    const pfCy = renderer.sizPlayfield.cy; // playfield rows — the input strip is cropped
    const mw = mapEl.clientWidth,
      mh = mapEl.clientHeight;
    if (mw === 0 || mh === 0) return;
    const scale = Math.min(mw / sz.cx, mh / pfCy); // fit the PLAYFIELD (not the full surface)
    const wrapW = Math.max(1, Math.round(sz.cx * scale)),
      wrapH = Math.max(1, Math.round(pfCy * scale));
    screenwrap.style.width = `${wrapW}px`;
    screenwrap.style.height = `${wrapH}px`; // clips the input strip
    canvas.style.width = `${wrapW}px`;
    canvas.style.height = `${Math.max(1, Math.round(sz.cy * scale))}px`;
  }
  function syncCanvas(): void {
    const sz = renderer.back.GetSize();
    if (canvas.width !== sz.cx || canvas.height !== sz.cy) {
      canvas.width = sz.cx;
      canvas.height = sz.cy;
      fitCanvas();
    }
  }
  function setZoom(z: number, ax?: number, ay?: number): void {
    const nz = Math.max(1, Math.min(maxZoom, Math.round(z * 100) / 100)); // never zoom IN past native
    if (nz === zoom) return;
    const rc = canvas.getBoundingClientRect();
    const ox = ax === undefined ? rc.left + rc.width / 2 : ax; // screen anchor (default = canvas center)
    const oy = ay === undefined ? rc.top + rc.height / 2 : ay;
    const o = renderer.back.GetSize(); // world point currently under the anchor
    const wx = sim.sim.m_wxView + coords.WcFromPc(Math.round((ox - rc.left) * (o.cx / rc.width)));
    const wy = sim.sim.m_wyView + coords.WcFromPc(Math.round((oy - rc.top) * (o.cy / rc.height)));
    zoom = nz;
    const s = surfaceFor(zoom);
    renderer.resize(s.cx, s.cy);
    syncCanvas();
    const rc2 = canvas.getBoundingClientRect(); // new display rect (CSS reflowed)
    const n = renderer.back.GetSize(); // keep that world point under the anchor
    setView(
      wx - coords.WcFromPc(Math.round((ox - rc2.left) * (n.cx / rc2.width))),
      wy - coords.WcFromPc(Math.round((oy - rc2.top) * (n.cy / rc2.height))),
    );
    present();
  }

  // spectator minimap (viewer-drawn, all sides) — see replay-viewer.ts.
  function mapWcSize(): { w: number; h: number } {
    const dims = sim.ctx.gobm.GetMapSize();
    return { w: coords.WcFromPc(dims.ctx * tile), h: coords.WcFromPc(dims.cty * tile) };
  }
  function drawMinimap(gobs: number[][]): void {
    const { w: mapW, h: mapH } = mapWcSize();
    const dims = sim.ctx.gobm.GetMapSize(); // map size in tiles
    const cw = minimap.width,
      ch = minimap.height;
    const pxX = cw / dims.ctx,
      pxY = ch / dims.cty; // minimap pixels per MAP TILE
    mmCtx.clearRect(0, 0, cw, ch);
    const trc = { left: 0, top: 0, right: 0, bottom: 0 };
    for (const g of gobs) {
      // [gid, side, type, wx, wy]
      if (g[2] === kgtScenery) continue; // scenery — not shown on the minimap
      const gob = sim.ctx.gobm.GetGob(g[0], false);
      if (!gob) continue;
      if (g[3] < 256 && g[4] < 256 && gob.GetFlags() & kfGobMobileUnit) continue; // in-production
      gob.GetTileRect(trc); // tiles the gob covers (1×1 unit … N×M structure)
      const w = Math.max(1, Math.round((trc.right - trc.left) * pxX));
      const h = Math.max(1, Math.round((trc.bottom - trc.top) * pxY));
      mmCtx.fillStyle = g[1] === 0 ? '#888' : sideCss(g[1]); // neutral = grey
      mmCtx.fillRect(Math.round(trc.left * pxX), Math.round(trc.top * pxY), w, h);
    }
    const vx = (sim.sim.m_wxView / mapW) * cw,
      vy = (sim.sim.m_wyView / mapH) * ch;
    const vw = (coords.WcFromPc(renderer.sizPlayfield.cx) / mapW) * cw;
    const vh = (coords.WcFromPc(renderer.sizPlayfield.cy) / mapH) * ch;
    mmCtx.strokeStyle = 'rgba(255,255,255,.9)';
    mmCtx.strokeRect(
      Math.round(vx) + 0.5,
      Math.round(vy) + 0.5,
      Math.max(1, Math.round(vw)),
      Math.max(1, Math.round(vh)),
    );
  }

  function present(throttleMinimap = false): void {
    if (fogSide === null && sim.sim.GetUpdateCount() !== lastReveal) {
      sim.ctx.fogm.RevealAll(); // neutral spectator: reveal all — only when the sim moved
      lastReveal = sim.sim.GetUpdateCount();
    }
    renderer.paint();
    syncCanvas();
    const back = renderer.back,
      sz = back.GetSize();
    if (glr !== null)
      glr.present((back as GlDibBitmap).data); // 1:1 (canvas buffer == back); CSS centres
    else ctx2d!.putImageData(new ImageData(new Uint8ClampedArray(back.rgba), sz.cx, sz.cy), 0, 0);
    const gobs = sim.dumpGobs(); // ONE gob snapshot per frame, shared by econ + minimap
    updateEcon(gobs); // refresh the per-side economy panel (innerHTML diffed)
    if (!throttleMinimap || mmTick++ % 4 === 0) drawMinimap(gobs); // minimap ≈15fps under play
  }

  // seek anywhere from the CHEAPEST base — see replay-viewer.ts for the strategy notes.
  function seekTo(targetUpdate: number): void {
    const t = Math.max(0, Math.min(lastBlock, Math.round(targetUpdate)));
    playing = false;
    const cur = sim.sim.GetUpdateCount();
    const cp = checkpoints.findLast((c) => c.blockPoint <= t); // kept sorted by addCheckpoint
    const cpBase = cp ? cp.blockPoint : -1;
    if (cur <= t && cur >= cpBase) {
      advanceUpdates(t - cur); // live sim is the closest base → step forward (no restore)
    } else {
      let ok = false; // restore the nearest checkpoint ≤ t, then fast-forward
      if (cp) {
        try {
          restoreFrom(cp);
          advanceUpdates(t - sim.sim.GetUpdateCount());
          ok = restoreMism === 0; // vs OUR re-sim, not the recording (which may have desynced)
        } catch {
          ok = false;
        }
        if (!ok) {
          // The restored checkpoint disagrees with our OWN from-seed re-sim → the snapshot is
          // missing state (a snapshot-completeness gap, like the Overmind bug). Drop it and
          // rebuild from seed — correct, but O(target) and the cause of "slow seeks despite
          // warmup". Surface it (badge + one console warning) so the next such gap is obvious.
          dropCheckpoint(cp);
          if (checkpointRestoreFails++ === 0) {
            console.warn(
              '[replay] checkpoint restore failed hash validation → rebuilding from seed ' +
                '(seeks will be slow). The sim snapshot is likely missing state — see ' +
                'sim/src/index.ts snapshot()/restore() and test/sim/snapshot-overmind-seek.test.ts.',
            );
          }
        }
      }
      if (!ok) buildAt(t);
    }
    playhead = sim.sim.GetUpdateCount(); // resume the playback clock from here
    // a seek teleports every projectile — drop the glide history (it would otherwise lerp
    // a shot from its pre-seek position clear across the map on the next paint)
    resetProjectileInterp();
    present();
  }

  // The sync-error indicator (dom.syncBadge). Two distinct signals, both silent before this:
  //   • checkpointRestoreFails — a seek fell back to a from-seed rebuild because the snapshot is
  //     missing state (the "slow seek" bug class). This is the primary thing to make obvious.
  //   • hashState.mism — the RECORDING's own per-block hashes disagree with our re-sim, i.e. the
  //     recorded game desynced (or we can't faithfully reproduce it).
  let lastBadgeKey = '';
  let desyncLatched = false; // hashState resets per re-sim; the recording's desync is a fixed fact
  function updateSyncBadge(): void {
    if (hashState.mism > 0) desyncLatched = true;
    const fails = checkpointRestoreFails;
    const key = `${fails}|${desyncLatched}`;
    if (key === lastBadgeKey) return; // only touch the DOM when the state changes
    lastBadgeKey = key;
    if (fails === 0 && !desyncLatched) {
      dom.syncBadge.style.display = 'none';
      return;
    }
    dom.syncBadge.style.display = '';
    const tip: string[] = [];
    if (fails > 0)
      tip.push(
        `${fails} checkpoint restore${fails > 1 ? 's' : ''} failed → rebuilding from seed ` +
          `(slow seeking). The sim snapshot is missing state — see the console.`,
      );
    if (desyncLatched)
      tip.push(
        `This recording's block hashes disagree with the re-sim — the recorded game desynced, ` +
          `or it can't be faithfully reproduced.`,
      );
    dom.syncBadge.textContent = fails > 0 ? '⚠ sync · slow seek' : '⚠ desync';
    dom.syncBadge.title = tip.join(' ');
  }

  // toggle which side's fog of war the map renders — see replay-viewer.ts.
  function setFogSide(s: number): void {
    const next = fogSide === s ? null : s;
    if (next === fogSide) return;
    fogSide = next;
    if (next !== null) {
      checkpoints.length = 0;
      cpSet.clear();
      warmFrontier = 0; // the cached snapshots hold the OLD side's fog
      nextCp = K_CP; // recapture them (with the new side's fog) during the rebuild
      const u = sim.sim.GetUpdateCount();
      buildAt(u); // re-sim from seed → accumulate `next`'s fog → resume here
      playhead = sim.sim.GetUpdateCount();
      startPrewarm(); // re-warm checkpoints for the new fog side (background)
    }
    present();
  }

  // --- unified pointer input (mouse + touch + pen) — see replay-viewer.ts (issue #109).
  const ptrs = new Map<number, { x: number; y: number }>(); // pointers currently down ON THE CANVAS
  let panning = false,
    panX = 0,
    panY = 0,
    panWx = 0,
    panWy = 0;
  let pinchDist = 0,
    pinchZoom = 1; // distance + zoom captured when the 2nd finger lands
  let scrubbing = false,
    scrubId = -1; // the pointer that owns the scrubber drag
  let mmDragging = false,
    mmId = -1; // the pointer that owns the minimap drag
  // a canvas press is a click-to-toggle-playback candidate until it pans past the slop or a
  // second finger lands (there's nothing on a replay's map to select, so a plain click plays/pauses)
  let tapOk = false,
    tapX = 0,
    tapY = 0;
  const TAP_SLOP = 6; // px of movement before a press stops being a click and becomes a pan
  const ptDist = (a: { x: number; y: number }, b: { x: number; y: number }): number =>
    Math.hypot(a.x - b.x, a.y - b.y);
  function beginPan(x: number, y: number): void {
    panning = true;
    panX = x;
    panY = y;
    panWx = sim.sim.m_wxView;
    panWy = sim.sim.m_wyView;
  }
  function startPinch(p: { x: number; y: number }[]): void {
    panning = false;
    pinchDist = ptDist(p[0], p[1]);
    pinchZoom = zoom;
  }
  canvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    // new gesture → drop any pointer/drag left stale by a MISSED pointerup/cancel (iOS).
    if (e.isPrimary) {
      ptrs.clear();
      scrubbing = false;
      mmDragging = false;
      scrubId = -1;
      mmId = -1;
    }
    canvas.setPointerCapture(e.pointerId);
    ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const p = [...ptrs.values()];
    if (p.length === 1) {
      beginPan(e.clientX, e.clientY);
      tapOk = e.button === 0; // primary press → click candidate (touch/pen report button 0)
      tapX = e.clientX;
      tapY = e.clientY;
    } else if (p.length >= 2) {
      startPinch(p); // start (or re-baseline) a pinch
      tapOk = false;
    }
  });

  // --- scrubber: the timeline bar. Drag-seeks are coalesced to one per frame.
  const bar = dom.scrub;
  bar.style.position = 'relative';
  bar.style.overflow = 'hidden';
  const warmFill = document.createElement('div');
  warmFill.style.cssText =
    'position:absolute;left:0;top:0;height:100%;width:0;background:#264;pointer-events:none';
  bar.appendChild(warmFill);
  const fill = document.createElement('div');
  fill.style.cssText =
    'position:absolute;left:0;top:0;height:100%;width:0;background:#3a8;pointer-events:none';
  bar.appendChild(fill); // appended after warmFill → drawn on top

  // ── timeline event markers (first contact / first battle / takeovers / mission end) ──
  // Detected by the prewarm pass (replay-events.ts) and merged here by key, so markers appear
  // progressively as the warm front advances and a restarted pass (fog change) re-merges
  // identical events. Hover a marker for the story, click it to jump there.
  const markerLayer = document.createElement('div');
  markerLayer.style.cssText = 'position:absolute;inset:0;pointer-events:none';
  bar.appendChild(markerLayer); // after the fills → markers draw on top
  const evMap = new Map<string, ReplayEvent>();
  const evTime = (u: number): string => {
    const s = Math.round((u * kcmsUpdate) / 1000); // game seconds (1× real time)
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };
  const sideName = (side: number): string =>
    rep.header.roster.find((r) => r.side === side)?.name || `side ${side}`;
  const structName = (ut: number | undefined): string => {
    // events only arrive from the prewarm pass, which starts after buildAt(0) built `sim`
    const c = ut === undefined ? undefined : sim.ctx.unitConsts[ut];
    return c ? c.szLongName || c.szName || 'structure' : 'structure';
  };
  function markerTitle(ev: ReplayEvent): string {
    const t = evTime(ev.update);
    switch (ev.kind) {
      case 'contact':
        return `${t} · first contact — ${sideName(ev.a)} ↔ ${sideName(ev.b!)}`;
      case 'battle':
        return `${t} · first battle — ${sideName(ev.a)} ⚔ ${sideName(ev.b!)}`;
      case 'takeover':
        return `${t} · hostile takeover — ${sideName(ev.a)} captured ${sideName(ev.b!)}'s ${structName(ev.ut)}`;
      case 'end':
        return `${t} · mission end — ${sideName(ev.a)} ${ev.win === true ? 'wins' : 'defeated'}`;
    }
  }
  function markerFace(ev: ReplayEvent): string {
    // side-colored faces: pair events split down the middle; a takeover wears the captor's
    // color under a white cap; mission end is the checkered flag.
    const a = sideCss(ev.a);
    switch (ev.kind) {
      case 'contact':
        return `background:linear-gradient(90deg,${a} 0 50%,${sideCss(ev.b!)} 50% 100%);opacity:.85`;
      case 'battle':
        return `background:linear-gradient(90deg,${a} 0 50%,${sideCss(ev.b!)} 50% 100%);box-shadow:inset 0 0 0 1px #fff`;
      case 'takeover':
        return `background:linear-gradient(180deg,#fff 0 4px,${a} 4px 100%)`;
      case 'end':
        return 'background:repeating-linear-gradient(45deg,#fff 0 2px,#000 2px 4px)';
    }
  }
  const KIND_RANK: Record<ReplayEvent['kind'], number> = {
    end: 3,
    takeover: 2,
    battle: 1,
    contact: 0,
  };
  function renderMarkers(): void {
    const frag = document.createDocumentFragment();
    const sorted = [...evMap.values()].sort((x, y) => x.update - y.update);
    // Events closer than a marker's width share ONE marker with a stacked tooltip (a mission
    // end reports winner + loser at the SAME update; first contact → first battle is often a
    // 2-second sequence) — separate stacked markers would hide all but the top tooltip. The
    // face comes from the weightiest kind, the click seeks to the FIRST moment so the whole
    // sequence plays out from there.
    const near = Math.max(1, lastBlock * 0.007); // ≈ one 7px marker width of a ~1000px bar
    for (let i = 0; i < sorted.length;) {
      let j = i + 1;
      while (j < sorted.length && sorted[j].update - sorted[i].update <= near) j++;
      const group = sorted.slice(i, j);
      i = j;
      const top = group.reduce((x, y) => (KIND_RANK[y.kind] > KIND_RANK[x.kind] ? y : x));
      const m = document.createElement('div');
      const pct = Math.max(0.4, Math.min(99.6, (group[0].update / Math.max(1, lastBlock)) * 100));
      m.style.cssText =
        `position:absolute;top:0;bottom:0;left:${pct}%;width:7px;transform:translateX(-50%);` +
        `border-radius:2px;cursor:pointer;pointer-events:auto;${markerFace(top)}`;
      m.title = group.map(markerTitle).join('\n');
      m.addEventListener('pointerdown', (e) => {
        // jump to the moment (stop the bar's own scrub handler — it would land nearby, not ON it)
        e.preventDefault();
        e.stopPropagation();
        pendingSeek = group[0].update;
        playing = false;
      });
      frag.appendChild(m);
    }
    markerLayer.replaceChildren(frag);
  }
  function addEvents(evs: ReplayEvent[]): void {
    let added = false;
    for (const ev of evs) {
      const k = eventKey(ev);
      if (!evMap.has(k)) {
        evMap.set(k, ev);
        added = true;
      }
    }
    if (added) renderMarkers();
  }

  // ── the expandable economy graph (📈): each side's credits over time, all in one plot ──
  // Samples come from the same prewarm pass that finds the markers (replay-events.ts), merged
  // by update so a superseded pass (fog change) re-fills identically. The panel shares the
  // scrubber's timeline (x = update 0…lastBlock), so a peak lines up with the map moment.
  const graphEl = dom.graph;
  const gcanvas = dom.graphCanvas;
  const gctx2d = gcanvas.getContext('2d')!;
  const econByUpdate = new Map<number, Record<number, number>>(); // update → side→credits
  const unitByUpdate = new Map<number, Record<number, number>>(); // update → side→unit count
  let econMax = 1; // running max credits across every sample (state() diagnostic)
  // per-side credit extremes — a side whose credits never move (a filler/eliminated AI that
  // never built or harvested, e.g. constant starting credits) is dropped from the graph.
  const econSideMin = new Map<number, number>();
  const econSideMax = new Map<number, number>();
  const unitSideMax = new Map<number, number>(); // per-side peak unit count (the RIGHT Y scale)
  let graphExpanded = false;
  // fixed plot insets (CSS px): the left gutter holds the unit-count axis labels (narrow
  // integers), the wider RIGHT gutter holds the credit labels ("6.0k"), the bottom holds the
  // time axis. Shared by the drawing, the click-to-seek mapping, and the playhead so all three
  // agree without depending on drawGraph.
  const G_LEFT = 34,
    G_RIGHT = 46,
    G_TOP = 8,
    G_BOTTOM = 15;
  // the plot's x-extent in CSS px. plotWidthPx() reads layout (for infrequent clicks);
  // the per-frame playhead uses cachedPlotW (refreshed by drawGraph) to avoid a reflow.
  const plotWidthPx = (): number =>
    Math.max(1, gcanvas.getBoundingClientRect().width - G_LEFT - G_RIGHT);
  let cachedPlotW = 1;
  const playLine = document.createElement('div'); // the graph's playhead (moved per frame)
  playLine.style.cssText =
    'position:absolute;top:0;bottom:0;width:1px;background:rgba(255,255,255,.55);pointer-events:none;display:none';
  graphEl.appendChild(playLine);

  // mobile (coarse pointer, matching main.ts prefersTouchControls) → a shorter panel so the
  // small screen keeps more of the map visible.
  const coarsePointer = ((): boolean => {
    try {
      return window.matchMedia('(pointer: coarse)').matches;
    } catch {
      return (navigator.maxTouchPoints ?? 0) > 0 || 'ontouchstart' in window;
    }
  })();
  function graphHeight(): number {
    const base = opts.mount.clientHeight || 480;
    const h = Math.min(200, Math.max(104, base * 0.3));
    return Math.round(coarsePointer ? (h * 2) / 3 : h);
  }
  const fmtCredits = (n: number): string =>
    n >= 1000 ? `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}k` : String(Math.round(n));

  function drawGraph(): void {
    if (!graphExpanded) return;
    const rect = gcanvas.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const cw = Math.round(rect.width * dpr),
      ch = Math.round(rect.height * dpr);
    if (gcanvas.width !== cw || gcanvas.height !== ch) {
      gcanvas.width = cw;
      gcanvas.height = ch;
    }
    gctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
    const Wc = rect.width,
      Hc = rect.height;
    gctx2d.clearRect(0, 0, Wc, Hc);
    const left = G_LEFT,
      right = G_RIGHT,
      top = G_TOP,
      bottom = G_BOTTOM;
    const plotW = Math.max(1, Wc - left - right),
      plotH = Math.max(1, Hc - top - bottom);
    cachedPlotW = plotW; // the per-frame playhead reads this (no reflow)
    // Dual Y-scales, both fitting only the graphed (active) sides so a dormant filler side
    // can't inflate an axis and flatten the real curves: unit counts on the LEFT (solid
    // lines), credits on the RIGHT (dashed lines). Both scales span the full plot height, so
    // the axis side is purely which labels annotate the shared gridlines.
    const shownSides = graphSides();
    const yMax = Math.max(1, ...shownSides.map((s) => econSideMax.get(s) ?? 0));
    const uMax = Math.max(1, ...shownSides.map((s) => unitSideMax.get(s) ?? 0));
    const xOf = (u: number): number => left + (u / Math.max(1, lastBlock)) * plotW;
    const yOf = (c: number): number => top + (1 - c / yMax) * plotH; // credits (right scale)
    const yOfU = (n: number): number => top + (1 - n / uMax) * plotH; // units (left scale)

    // shared horizontal gridlines; unit-count labels on the left, credit labels on the right
    gctx2d.lineWidth = 1;
    gctx2d.font = '9px ui-monospace,monospace';
    gctx2d.textBaseline = 'middle';
    for (let g = 0; g <= 4; g++) {
      const y = Math.round(top + (1 - g / 4) * plotH) + 0.5;
      gctx2d.strokeStyle = 'rgba(120,200,150,.16)';
      gctx2d.beginPath();
      gctx2d.moveTo(left, y);
      gctx2d.lineTo(left + plotW, y);
      gctx2d.stroke();
      gctx2d.fillStyle = '#5a7'; // units (left)
      gctx2d.textAlign = 'right';
      gctx2d.fillText(String(Math.round((uMax * g) / 4)), left - 5, top + (1 - g / 4) * plotH);
      gctx2d.fillStyle = '#8ac'; // credits (right)
      gctx2d.textAlign = 'left';
      gctx2d.fillText(fmtCredits((yMax * g) / 4), left + plotW + 5, top + (1 - g / 4) * plotH);
    }
    // subtle vertical guides where timeline events happen (ties the graph to the markers)
    gctx2d.strokeStyle = 'rgba(255,255,255,.07)';
    for (const ev of evMap.values()) {
      const x = Math.round(xOf(ev.update)) + 0.5;
      gctx2d.beginPath();
      gctx2d.moveTo(x, top);
      gctx2d.lineTo(x, top + plotH);
      gctx2d.stroke();
    }
    // x time ticks (game minutes:seconds at 1×)
    gctx2d.fillStyle = '#5a7';
    gctx2d.textBaseline = 'bottom';
    for (let g = 0; g <= 4; g++) {
      const u = (lastBlock * g) / 4;
      gctx2d.textAlign = g === 0 ? 'left' : g === 4 ? 'right' : 'center';
      gctx2d.fillText(evTime(u), Math.max(left, Math.min(left + plotW, xOf(u))), Hc);
    }

    // per side: a SOLID credits line (left scale) + a DASHED unit-count line (right scale),
    // both in the side's color. One helper plots a (value-by-update → y) series.
    const updates = [...econByUpdate.keys()].sort((a, b) => a - b);
    const plotSeries = (
      by: Map<number, Record<number, number>>,
      side: number,
      toY: (v: number) => number,
    ): void => {
      gctx2d.beginPath();
      let started = false;
      for (const u of updates) {
        const v = by.get(u)?.[side];
        if (v === undefined) continue;
        const px = xOf(u),
          py = toY(v);
        if (started) gctx2d.lineTo(px, py);
        else {
          gctx2d.moveTo(px, py);
          started = true;
        }
      }
      if (started) gctx2d.stroke();
    };
    if (updates.length >= 2) {
      gctx2d.lineWidth = 1.5;
      gctx2d.lineJoin = 'round';
      for (const side of shownSides) {
        gctx2d.strokeStyle = sideCss(side);
        gctx2d.setLineDash([3, 3]); // credits: dashed
        plotSeries(econByUpdate, side, yOf);
        gctx2d.setLineDash([]); // units: solid
        plotSeries(unitByUpdate, side, yOfU);
      }
      gctx2d.setLineDash([]);
    }

    // legend: side chips + names, then a key for the two line styles (solid $ / dashed units)
    gctx2d.font = '9px ui-monospace,monospace';
    gctx2d.textAlign = 'left';
    gctx2d.textBaseline = 'middle';
    let lx = left + 6;
    const chip = (fill: string, label: string, drawSwatch: () => void): void => {
      const wLabel = gctx2d.measureText(label).width;
      gctx2d.fillStyle = 'rgba(10,20,16,.72)';
      gctx2d.fillRect(lx - 2, top + 1, 10 + wLabel + 6, 12);
      gctx2d.fillStyle = fill;
      drawSwatch();
      gctx2d.fillStyle = '#cfc';
      gctx2d.fillText(label, lx + 10, top + 7.5);
      lx += 10 + wLabel + 12;
    };
    for (const s of shownSides) {
      const nm = sideName(s);
      chip(sideCss(s), nm.length > 9 ? `${nm.slice(0, 9)}…` : nm, () =>
        gctx2d.fillRect(lx, top + 4, 7, 7),
      );
    }
    // the encoding key: a dashed stroke = credits ($), a solid stroke = units
    const keyLine = (dash: number[], label: string): void => {
      const wLabel = gctx2d.measureText(label).width;
      gctx2d.fillStyle = 'rgba(10,20,16,.72)';
      gctx2d.fillRect(lx - 2, top + 1, 12 + wLabel + 6, 12);
      gctx2d.strokeStyle = '#cfc';
      gctx2d.lineWidth = 1.5;
      gctx2d.setLineDash(dash);
      gctx2d.beginPath();
      gctx2d.moveTo(lx, top + 7.5);
      gctx2d.lineTo(lx + 9, top + 7.5);
      gctx2d.stroke();
      gctx2d.setLineDash([]);
      gctx2d.fillStyle = '#cfc';
      gctx2d.fillText(label, lx + 12, top + 7.5);
      lx += 12 + wLabel + 12;
    };
    keyLine([3, 3], '$');
    keyLine([], 'units');
  }

  // Coalesce redraws to at most ONE per animation frame. The background prewarm streams
  // sample batches in bursts (every worker checkpoint message, or every in-thread idle
  // slice), and redrawing the whole canvas on each one competes with the playback loop on
  // the main thread — the graph would jank playback while it's warming. Scheduling collapses
  // a burst into a single redraw at the next frame boundary.
  let graphDrawScheduled = false;
  function scheduleGraphDraw(): void {
    if (graphDrawScheduled || !graphExpanded) return;
    graphDrawScheduled = true;
    requestAnimationFrame(() => {
      graphDrawScheduled = false;
      drawGraph();
    });
  }

  function addSamples(smp: EconomySample[]): void {
    let changed = false;
    for (const s of smp) {
      if (!econByUpdate.has(s.update)) {
        econByUpdate.set(s.update, s.credits);
        unitByUpdate.set(s.update, s.units);
        for (const k of Object.keys(s.credits)) {
          const side = Number(k),
            c = s.credits[side];
          econMax = Math.max(econMax, c);
          econSideMax.set(side, Math.max(econSideMax.get(side) ?? -Infinity, c));
          econSideMin.set(side, Math.min(econSideMin.get(side) ?? Infinity, c));
        }
        for (const k of Object.keys(s.units)) {
          const side = Number(k);
          unitSideMax.set(side, Math.max(unitSideMax.get(side) ?? 0, s.units[side]));
        }
        changed = true;
      }
    }
    if (changed) scheduleGraphDraw(); // coalesced → one redraw per frame, not per sample batch
  }
  // sides worth graphing: those whose credits actually MOVE over the game (a real economy
  // that builds/harvests/spends). Drops filler or eliminated-at-start AI sitting at a flat
  // constant — they add legend noise and a meaningless horizontal line.
  const graphSides = (): number[] =>
    sides.filter((s) => s > 0 && (econSideMax.get(s) ?? 0) > (econSideMin.get(s) ?? 0));

  const MM_GAP = 8; // px of clearance between the lifted minimap and the graph's top edge
  function setGraph(open: boolean): void {
    graphExpanded = open;
    const h = open ? graphHeight() : 0;
    dom.chart.setAttribute('aria-expanded', String(open));
    dom.chart.style.background = open ? '#1a4d2e' : '#123';
    playLine.style.display = open ? 'block' : 'none';
    // the minimap is anchored to the mount's bottom-right and would overlap the graph; lift it
    // to float over the (shrunken) gameplay area, a fixed gap above the graph's top edge,
    // leaving the graph full width. MEASURE the strip below the graph (status + transport +
    // safe-area) while the panel is still collapsed rather than guessing it — it varies with
    // viewport/safe-area, and a wrong guess is exactly what left a few px of overlap.
    if (open) {
      const stripBelow = Math.max(
        0,
        opts.mount.getBoundingClientRect().bottom - graphEl.getBoundingClientRect().bottom,
      );
      // stripBelow already includes the transport's safe-area margin → no extra env() here.
      minimap.style.bottom = `${Math.round(h + stripBelow + MM_GAP)}px`;
    } else {
      minimap.style.bottom = 'calc(env(safe-area-inset-bottom,0px) + 36px)'; // back above the transport
    }
    graphEl.style.height = `${h}px`;
    // the canvas is full-width from the moment it's shown (only the height animates), so seed the
    // playhead's cached width now — otherwise it sits pinned at the left until the first real
    // drawGraph (which no-ops on the zero-height canvas mid-transition).
    if (open) {
      cachedPlotW = plotWidthPx();
      requestAnimationFrame(() => drawGraph()); // first paint (no-transition case)
    }
  }
  dom.chart.addEventListener('click', () => setGraph(!graphExpanded));
  // the panel animates height over .16s — repaint at the FINAL size (one rAF fires while it's
  // still collapsed and drawGraph would no-op on a zero-height canvas)
  graphEl.addEventListener('transitionend', () => drawGraph());

  // click / drag the graph to scrub (same timeline as the scrubber). Geometry comes from the
  // live canvas rect (not a cached draw) so an early click — before the first paint — still
  // maps correctly.
  let gScrub = false,
    gScrubId = -1;
  const graphSeekFrom = (e: PointerEvent): void => {
    const r = gcanvas.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - r.left - G_LEFT) / plotWidthPx()));
    pendingSeek = frac * lastBlock;
    playing = false;
  };
  gcanvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    gScrub = true;
    gScrubId = e.pointerId;
    graphSeekFrom(e); // seek FIRST — a failed capture must not swallow the click
    try {
      gcanvas.setPointerCapture(e.pointerId);
    } catch {
      /* no active pointer (synthetic event) — the drag just won't track, the seek still lands */
    }
  });
  gcanvas.addEventListener('pointermove', (e) => {
    if (gScrub && e.pointerId === gScrubId) graphSeekFrom(e);
  });
  const endGraphScrub = (e: PointerEvent): void => {
    if (e.pointerId === gScrubId) {
      gScrub = false;
      gScrubId = -1;
    }
  };
  gcanvas.addEventListener('pointerup', endGraphScrub);
  gcanvas.addEventListener('pointercancel', endGraphScrub);
  gcanvas.addEventListener('lostpointercapture', endGraphScrub);

  const scrubFrom = (e: MouseEvent): void => {
    // scrub: queue one synchronous seek to the bar position
    const r = bar.getBoundingClientRect();
    pendingSeek = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * lastBlock;
    playing = false;
  };
  bar.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    bar.setPointerCapture(e.pointerId);
    scrubbing = true;
    scrubId = e.pointerId;
    scrubFrom(e);
  });
  const endScrub = (e: PointerEvent): void => {
    if (e.pointerId === scrubId) {
      scrubbing = false;
      scrubId = -1;
    }
  };
  bar.addEventListener('pointerup', endScrub);
  bar.addEventListener('pointercancel', endScrub);
  bar.addEventListener('lostpointercapture', endScrub); // backstop for a missed scrub-up
  // gentle, magnitude-proportional zoom anchored at the cursor.
  canvas.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      setZoom(zoom * Math.exp(e.deltaY * 0.0008), e.clientX, e.clientY);
    },
    { passive: false },
  );

  // --- background pre-warm (worker preferred, idle-sliced in-thread fallback) — see replay-viewer.ts.
  function scheduleIdle(fn: () => void): void {
    // idle when supported (desktop), else a gentle timer (iOS)
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void;
    };
    if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 1000 });
    else setTimeout(fn, 16);
  }
  let prewarmGen = 0;
  let prewarmWorker: Worker | null | undefined; // undefined = not yet tried; null = fallback
  function startPrewarm(): void {
    const gen = ++prewarmGen; // supersedes any earlier prewarm (e.g. on fog change)
    const roster = rosterFor(fogSide ?? localSide);
    if (prewarmWorker === undefined) {
      // first call: try to stand up the worker once (skipped when the host disabled it)
      if (prewarmWorkerUrl === null) {
        prewarmWorker = null;
      } else
        try {
          const w = new Worker(prewarmWorkerUrl);
          w.onmessage = (e: MessageEvent): void => {
            const msg = e.data as {
              type: string;
              gen: number;
              blockPoint?: number;
              snap?: Uint8Array;
              hashes?: number[]; // [block, hash, …] pairs since the last message (ground truth)
              events?: ReplayEvent[]; // timeline key moments detected since then
              samples?: EconomySample[]; // per-side economy snapshots since then
            };
            if (msg.gen !== prewarmGen) return; // a newer fog side superseded this pass → ignore
            if (msg.hashes) {
              for (let i = 0; i + 1 < msg.hashes.length; i += 2) {
                if (!simHashByBlock.has(msg.hashes[i]))
                  simHashByBlock.set(msg.hashes[i], msg.hashes[i + 1] >>> 0);
              }
            }
            if (msg.events) addEvents(msg.events);
            if (msg.samples) addSamples(msg.samples);
            if (msg.type === 'cp' && msg.snap) addCheckpoint(msg.blockPoint!, msg.snap);
            else if (msg.type === 'done')
              warmFrontier = lastBlock; // whole game warmed → fill the bar to 100%
            else if (msg.type === 'error') {
              console.warn('[replay] prewarm worker failed → in-thread fallback', msg);
              w.terminate();
              prewarmWorker = null;
              startPrewarmInThread(prewarmGen);
            }
          };
          w.onerror = (): void => {
            w.terminate();
            prewarmWorker = null;
            startPrewarmInThread(prewarmGen);
          };
          prewarmWorker = w;
        } catch {
          prewarmWorker = null;
        } // no Worker constructor → permanent fallback
    }
    if (prewarmWorker) {
      try {
        prewarmWorker.postMessage({
          type: 'start',
          gen,
          wirepBytes,
          levelData,
          templates,
          anir,
          seed: rep.header.seed,
          roster,
          tile,
          kcp: K_CP,
        });
      } catch {
        prewarmWorker.terminate();
        prewarmWorker = null;
        startPrewarmInThread(gen);
      } // e.g. DataCloneError
    } else {
      startPrewarmInThread(gen);
    }
  }
  // fallback warmer: the original idle-sliced in-thread pass. Tracking stays ON (like the worker)
  // so the pass also lays down ground-truth hashes for checkpoint-restore validation.
  function startPrewarmInThread(gen: number): void {
    let warm: Sim | null = null,
      wcursor = 0,
      wnext = K_CP;
    const collector = makeEventCollector(sides); // timeline key moments (like the worker pass)
    const slice = (): void => {
      if (gen !== prewarmGen) return; // superseded / stopped → drop the throwaway sim
      if (!warm) {
        warm = Sim.create({
          levelName: lvl,
          levelData,
          templates,
          anir,
          seed: rep.header.seed,
          simVersion: rep.header.simVersion,
          roster: rosterFor(fogSide ?? localSide),
          multiplayer: true,
          gcxTile: SIM_TILE,
          host: { ...fogHost, ...collector.host },
        });
        collector.attach(warm);
        warm.onBlockHash = (block, hash) => {
          if (!simHashByBlock.has(block)) simHashByBlock.set(block, hash >>> 0);
        };
      }
      const deadline = performance.now() + 5; // ≤5ms per slice → stays out of playback's way
      while (performance.now() < deadline) {
        if (warm.stepOnce()) {
          collector.pollUpdate(); // one update advanced → first-contact mask diff
          continue;
        }
        if (warm.sim.GetUpdateCount() >= wnext) {
          addCheckpoint(warm.resumeBlockPoint(), warm.snapshot());
          wnext = warm.sim.GetUpdateCount() + K_CP;
        }
        if (wcursor >= rep.blocks.length) {
          warm = null;
          warmFrontier = lastBlock;
          addEvents(collector.drain());
          addSamples(collector.drainSamples());
          return;
        } // end → fully warmed
        warm.queueBlock(rep.blocks[wcursor].block, rep.blocks[wcursor].cmds);
        wcursor++;
      }
      addEvents(collector.drain()); // surface markers incrementally as the warm front advances
      addSamples(collector.drainSamples()); // …and the economy series
      scheduleIdle(slice); // more to do → yield, resume next idle slice
    };
    scheduleIdle(slice);
  }

  // --- the local playback clock (the viewer owns it: play = stepBlock + paint)
  buildAt(0);
  recenter(); // initial framing on the action
  const m = sim.ctx.gobm.GetMapSize(); // { ctx, cty } in tiles (used for the minimap below)
  maxZoom = computeMaxZoom();
  // size the minimap to the map aspect; click/DRAG it to move the camera, WHEEL to zoom.
  const MM = Math.max(96, Math.min(196, Math.round(Math.min(innerWidth, innerHeight) * 0.3)));
  minimap.width = m.ctx >= m.cty ? MM : Math.max(48, Math.round((MM * m.ctx) / m.cty));
  minimap.height = m.cty >= m.ctx ? MM : Math.max(48, Math.round((MM * m.cty) / m.ctx));
  const mmWorld = (e: MouseEvent): { wx: number; wy: number } => {
    const rc = minimap.getBoundingClientRect();
    const { w: mapW, h: mapH } = mapWcSize();
    const fx = Math.max(0, Math.min(1, (e.clientX - rc.left) / rc.width));
    const fy = Math.max(0, Math.min(1, (e.clientY - rc.top) / rc.height));
    return { wx: Math.round(fx * mapW), wy: Math.round(fy * mapH) };
  };
  const mmCenterOn = (wx: number, wy: number): void => {
    setView(
      wx - coords.WcFromPc(Math.floor(renderer.sizPlayfield.cx / 2)),
      wy - coords.WcFromPc(Math.floor(renderer.sizPlayfield.cy / 2)),
    );
  };
  minimap.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    minimap.setPointerCapture(e.pointerId);
    mmDragging = true;
    mmId = e.pointerId;
    const p = mmWorld(e);
    mmCenterOn(p.wx, p.wy);
    present();
  });
  const endMm = (e: PointerEvent): void => {
    if (e.pointerId === mmId) {
      mmDragging = false;
      mmId = -1;
    }
  };
  minimap.addEventListener('pointerup', endMm);
  minimap.addEventListener('pointercancel', endMm);
  minimap.addEventListener('lostpointercapture', endMm);
  minimap.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      const p = mmWorld(e); // zoom toward the point under the minimap cursor
      setZoom(zoom * Math.exp(e.deltaY * 0.0008));
      mmCenterOn(p.wx, p.wy);
      present();
    },
    { passive: false },
  );

  // unified pointer move/up (priority: scrubbing > minimap > pinch > pan) — window-level with
  // pointer capture, tracked for teardown.
  onWin('pointermove', (e: PointerEvent) => {
    if (scrubbing && e.pointerId === scrubId) {
      scrubFrom(e);
      return;
    }
    if (mmDragging && e.pointerId === mmId) {
      const p = mmWorld(e);
      mmCenterOn(p.wx, p.wy);
      present();
      return;
    }
    if (!ptrs.has(e.pointerId)) return; // a pointer that didn't start on the canvas
    ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (tapOk && Math.hypot(e.clientX - tapX, e.clientY - tapY) > TAP_SLOP) tapOk = false;
    const p = [...ptrs.values()];
    if (p.length >= 2) {
      // pinch: fingers apart (distance ↑) → zoom IN (z ↓)
      const d = ptDist(p[0], p[1]);
      if (d > 0) setZoom(pinchZoom * (pinchDist / d), (p[0].x + p[1].x) / 2, (p[0].y + p[1].y) / 2);
    } else if (panning) {
      // single-pointer drag → pan the camera
      const sz = renderer.back.GetSize(); // the displayed content is the back surface
      const rc = canvas.getBoundingClientRect();
      const dx = Math.round((e.clientX - panX) * (sz.cx / rc.width));
      const dy = Math.round((e.clientY - panY) * (sz.cy / rc.height));
      setView(panWx - coords.WcFromPc(dx), panWy - coords.WcFromPc(dy));
      present();
    }
  });
  function dropCanvasPointer(e: PointerEvent): void {
    // a canvas pan/pinch pointer lifted or lost capture
    if (!ptrs.delete(e.pointerId)) return;
    const p = [...ptrs.values()];
    if (p.length >= 2)
      startPinch(p); // 3→2 (rare): re-baseline the pinch
    else if (p.length === 1)
      beginPan(p[0].x, p[0].y); // 2→1: resume panning from the remaining finger
    else {
      // 1→0: idle. A press that never panned past the slop or pinched is a plain click on the
      // map — nothing there is selectable in a replay, so it toggles playback (like Space).
      panning = false;
      pinchDist = 0;
      if (tapOk && e.type === 'pointerup') togglePlayback();
      tapOk = false;
    }
  }
  canvas.addEventListener('pointerup', dropCanvasPointer);
  canvas.addEventListener('pointercancel', dropCanvasPointer);
  canvas.addEventListener('lostpointercapture', dropCanvasPointer); // iOS missed-up backstop
  // the letterbox bars around the canvas count as "the replay" too — a plain click there
  // (same slop rule, nothing to select or drag) also toggles playback.
  let mapTapOk = false,
    mapTapX = 0,
    mapTapY = 0;
  mapEl.addEventListener('pointerdown', (e) => {
    mapTapOk = e.target === mapEl && e.isPrimary && e.button === 0;
    mapTapX = e.clientX;
    mapTapY = e.clientY;
  });
  mapEl.addEventListener('pointerup', (e) => {
    if (
      mapTapOk &&
      e.target === mapEl &&
      Math.hypot(e.clientX - mapTapX, e.clientY - mapTapY) <= TAP_SLOP
    )
      togglePlayback();
    mapTapOk = false;
  });
  fitCanvas();
  // on resize/rotate the map aspect changes → refit + coalesced surface reflow.
  let reflowPending = false;
  const ro = new ResizeObserver(() => {
    fitCanvas();
    if (reflowPending) return;
    reflowPending = true;
    requestAnimationFrame(() => {
      reflowPending = false;
      if (stopped) return;
      maxZoom = computeMaxZoom();
      zoom = Math.min(zoom, maxZoom);
      const s = surfaceFor(zoom),
        cur = renderer.back.GetSize();
      if (s.cx !== cur.cx || s.cy !== cur.cy) {
        renderer.resize(s.cx, s.cy);
        syncCanvas();
      }
      fitCanvas();
      present();
      if (graphExpanded) drawGraph(); // the panel width tracks the mount → repaint the series
    });
  });
  ro.observe(mapEl);
  setZoom(maxZoom); // fit the whole map on first load (same as 'f')

  // --- transport controls (play/pause + speed menu) and the per-side fog toggles ----------------
  if (opts.browse === false) {
    dom.browse.style.display = 'none';
  } else {
    initReplayBrowse(dom.browse, {
      apiBase: opts.browse?.apiBase,
      current: opts.browse?.currentKey ?? null,
      onPick: opts.browse?.onPick,
    });
  }
  const playBtn = dom.playpause;
  const speedSel = dom.speedSel;
  speedSel.innerHTML = SPEEDS.map((s) => `<option value="${s}">${speedLabel(s)}</option>`).join('');
  speedSel.value = String(speed);
  // "at the end" = every recorded block has been consumed (see replay-viewer.ts).
  const atEnd = (): boolean => cursor >= rep.blocks.length;
  // shared by the ▶ button and a plain click on the map (dropCanvasPointer).
  function togglePlayback(): void {
    if (atEnd()) {
      seekTo(0);
      playing = true;
    } // at the end the toggle restarts (like 'r')
    else playing = !playing;
  }
  playBtn.addEventListener('click', togglePlayback);
  speedSel.addEventListener('change', () => {
    speed = parseFloat(speedSel.value) || speed;
  });

  // ── the initial "click to start" play-button overlay ────────────────────────────────
  // The viewer starts paused (see `playing` above): a big translucent play button covers the
  // game area. Clicking it is the user gesture that unlocks the AudioContext (replay-audio.ts's
  // window listeners fire on the same click) AND flips playback on, so sound plays from the very
  // first frame instead of being dropped by the browser's autoplay policy. It's a one-shot gate —
  // frame() removes it the moment playback starts or the sim advances by any other route.
  const playOverlay = document.createElement('div');
  playOverlay.className = 'wirv-play-overlay';
  playOverlay.style.cssText =
    'position:absolute;inset:0;z-index:9;display:flex;align-items:center;justify-content:center;' +
    'background:rgba(0,0,0,.35);cursor:pointer;touch-action:manipulation';
  playOverlay.setAttribute('role', 'button');
  playOverlay.setAttribute('aria-label', 'Play replay');
  const playCircle = document.createElement('div');
  playCircle.className = 'wirv-play-btn';
  playCircle.style.cssText =
    'width:92px;height:92px;border-radius:50%;background:rgba(10,20,16,.82);border:2px solid #0a0;' +
    'display:flex;align-items:center;justify-content:center;box-shadow:0 4px 24px rgba(0,0,0,.55);' +
    'transition:transform .12s ease,background .12s ease';
  const playTri = document.createElement('div'); // a CSS triangle, nudged right to look centered
  playTri.style.cssText =
    'width:0;height:0;margin-left:8px;border-style:solid;border-width:22px 0 22px 36px;' +
    'border-color:transparent transparent transparent #8f8';
  playCircle.appendChild(playTri);
  playOverlay.appendChild(playCircle);
  dom.map.appendChild(playOverlay);
  playOverlay.addEventListener('click', (e) => {
    e.preventDefault();
    playOverlay.remove(); // frame() would also remove it once `playing`, but do it eagerly
    playing = true;
  });
  dom.close?.addEventListener('click', () => opts.onExit?.());
  // the fog boxes live inside the econ strip, whose innerHTML is rebuilt every frame — delegate.
  econ.addEventListener('pointerdown', (e) => {
    const t = (e.target as HTMLElement).closest('[data-fog]') as HTMLElement | null;
    if (!t) return;
    e.preventDefault();
    setFogSide(parseInt(t.getAttribute('data-fog')!, 10));
  });
  present();

  // The scrubber scrubs GAME TIME — the superset of sim time. `t` is a fractional update
  // count: the sim lands on the update at-or-before it and the remainder positions the tick
  // clock, so the projectile glide shows the in-between pose. Unlike the 3D viewer's stateful
  // effects, the 2D presentation is a pure function of (sim state, clock) — every combat
  // effect is a sim gob, so a landing is never sterile and even BACKWARD sub-update drags
  // rewind cleanly (the interp fraction just clamps). Grabbing the bar pauses; ▶ resumes.
  function scrubTo(t: number): void {
    playing = false;
    const U = Math.floor(t);
    if (U !== sim.sim.GetUpdateCount()) seekTo(U); // sim jump (sets playhead = U, resets interp)
    playhead = Math.max(U, t); // the sub-update remainder — the tick clock lands EXACTLY on t
  }
  let prevT = 0; // previous rAF timestamp — the game-clock integration delta
  function frame(now: number): void {
    if (stopped) return;
    const realDt = prevT === 0 ? 16 : Math.min(120, Math.max(0, now - prevT));
    prevT = now;
    // scrub = one synchronous seek to the bar position per frame (coalesced).
    if (pendingSeek !== null) {
      const t = pendingSeek;
      pendingSeek = null;
      scrubTo(t);
    }
    // integrate the game clock: play advances it at `speed`× real time (frame-rate
    // independent), queued steps (. / > / →) advance it while paused; the sim follows
    // whenever the playhead crosses a whole update. Paused with nothing queued, the clock
    // holds — pause IS freeze (shots hang mid-glide; there is nothing else wall-clocked).
    const dGame = (playing ? realDt * speed : 0) + stepGameMs;
    stepGameMs = 0;
    let stepped = false;
    if (dGame > 0) {
      playhead = Math.max(playhead, sim.sim.GetUpdateCount()) + dGame / kcmsUpdate;
      const target = Math.floor(playhead);
      if (target > sim.sim.GetUpdateCount()) {
        sfxGate = true; // clock-driven forward stepping IS live play → audible
        try {
          stepTo(target);
        } finally {
          sfxGate = false; // a throw must not latch the gate open for later seeks/re-sims
        }
        stepped = true;
      }
      if (playing && cursor >= rep.blocks.length) playing = false; // end of the recording
    }
    // paint when the sim stepped OR the clock moved — the glide's in-between positions only
    // exist on a paint (this is the viewer's analogue of the game's kfRedrawMax pump)
    if (stepped || playhead !== lastPlayhead) {
      lastPlayhead = playhead;
      present(true);
    }
    const u = sim.sim.GetUpdateCount();
    const frac = lastBlock ? Math.max(0, Math.min(100, (u / lastBlock) * 100)) : 100;
    fill.style.width = `${frac}%`; // fractional → the bar tracks every update
    warmFill.style.width = lastBlock
      ? `${Math.max(0, Math.min(100, (warmFrontier / lastBlock) * 100))}%`
      : '0'; // prewarm reach
    if (graphExpanded)
      playLine.style.left = `${G_LEFT + (lastBlock ? u / lastBlock : 0) * cachedPlotW}px`; // graph playhead
    // drop the "click to start" overlay once playback is under way by ANY route (the overlay
    // click, the ▶ button, Space, or a headless-driver seek/advance that moves off update 0).
    if (playOverlay.isConnected && (playing || u > 0)) playOverlay.remove();
    const ended = atEnd(); // at 100% the play/pause button becomes a restart button
    playBtn.textContent = ended ? '↻' : playing ? '⏸' : '▶';
    playBtn.title = ended ? 'restart (r)' : 'play / pause (Space)';
    if (speedSel.value !== String(speed)) speedSel.value = String(speed);
    setStatus(`${lvl} · ${Math.round(frac)}%${ended ? ' · ■ end' : ''}`);
    updateSyncBadge();
    rafId = requestAnimationFrame(frame);
  }
  rafId = requestAnimationFrame(frame);
  startPrewarm(); // background-fill every checkpoint so the first seek is fast

  // --- keyboard controls (window-level, tracked for teardown)
  let helpEl: HTMLElement | null = null;
  onWin('keydown', (e: KeyboardEvent) => {
    const u = sim.sim.GetUpdateCount();
    if (e.key === ' ') {
      playing = !playing;
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      // exactly one sim update, through the game clock — frame()'s integration steps the
      // sim (audible, like play) and the glide crosses the whole inter-update gap
      playing = false;
      stepGameMs += kcmsUpdate;
    } else if (e.key === 'ArrowLeft') {
      playing = false;
      seekTo(u - 6);
    } else if (e.key === '.' || e.key === '>') {
      // step paused time: . = one display frame (sub-update — shots crawl mid-glide),
      // > = one sim update. Hold . to crawl; the sim follows whenever an update's worth
      // of game time accumulates. Stepping implies pausing.
      playing = false;
      stepGameMs += e.key === '.' ? 1000 / 60 : kcmsUpdate;
    } else if (e.key === '+' || e.key === '=') speed = Math.min(256, speed * 2);
    else if (e.key === '-' || e.key === '_') speed = Math.max(1 / 16, speed / 2);
    else if (e.key === '[')
      setZoom(zoom * 1.3); // zoom out
    else if (e.key === ']')
      setZoom(zoom / 1.3); // zoom in
    else if (e.key.toLowerCase() === 'f')
      setZoom(maxZoom); // fit the whole map
    else if (e.key.toLowerCase() === 'r') {
      seekTo(0);
      playing = true;
    } else if (e.key.toLowerCase() === 'c') {
      recenter();
      present();
    } // center on the action
    else if (e.key.toLowerCase() === 'g') {
      setGraph(!graphExpanded);
    } // toggle the economy graph
    else if (e.key === '?') {
      e.preventDefault();
      toggleHelp();
    } // controls panel
    else if (e.key === 'Escape') {
      if (helpEl !== null) {
        helpEl.remove();
        helpEl = null;
      } else if (opts.onExit !== undefined) {
        e.preventDefault();
        opts.onExit(); // embedded mode: Escape closes the viewer
      }
    }
  });

  function toggleHelp(): void {
    if (helpEl !== null) {
      helpEl.remove();
      helpEl = null;
      return;
    }
    const h = document.createElement('div');
    h.style.cssText =
      'position:fixed;inset:0;z-index:30;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.82);color:#cfc;font:13px ui-monospace,monospace;cursor:pointer';
    const rows: [string, string][] = [
      ['▶ / Space', 'play / pause — pausing freezes time (shots hold mid-flight)'],
      ['speed menu', 'game speed (or - / +) — 1× = real time, below 1× slow motion'],
      ['← / →', 'step back / forward one sim update'],
      ['. / >', 'step paused time +1 frame / +1 update — shots glide sub-update'],
      ['[ / ]', 'zoom out / in'],
      ['f', 'fit whole map'],
      ['c', 'center on the action'],
      [
        'g  /  📈',
        'economy graph — each side’s unit count (solid, left) + credits (dashed, right)',
      ],
      ['r', 'restart'],
      ['scrubber', 'scrub game time (sub-update precise while paused)'],
      [
        'markers',
        'key events (first contact/battle, takeovers, end) — hover for details, click to jump',
      ],
      ['fog box', "the box by a side's chip → render that side's fog (click again = off)"],
      ['map', 'click to play/pause · drag to pan · wheel to zoom'],
      ['minimap', 'click / drag to move · wheel to zoom'],
      ['☰ button', 'browse the recording archive'],
      ['drop a .wirep', 'load another recording'],
      ['?  /  Esc', 'this panel / close'],
    ];
    h.innerHTML = `<div style="background:#111;border:1px solid #0a0;border-radius:6px;padding:16px 22px;cursor:default" onclick="event.stopPropagation()">
      <div style="color:#8f8;font-size:15px;margin-bottom:10px">WI replay viewer — controls</div>
      <table style="border-spacing:14px 4px">${rows.map(([k, v]) => `<tr><td style="color:#7c7;text-align:right;white-space:nowrap"><b>${k}</b></td><td>${v}</td></tr>`).join('')}</table>
      <div style="color:#6a6;font-size:11px;margin-top:10px">URL: ?wirep=&lt;url&gt; · &amp;fog · &amp;speed=N · &amp;side=N · &amp;art=832|824|2432</div></div>`;
    h.onclick = () => {
      h.remove();
      helpEl = null;
    };
    document.body.appendChild(h);
    helpEl = h;
  }

  // the control surface: returned to the embedder AND published as window.wiReplay for the
  // headless drivers (replay-viewer.mjs oracle / claude-in-chrome). advance/seek repaint.
  const handle: ReplayHandle = {
    state: () => ({
      update: sim.sim.GetUpdateCount(),
      lastBlock,
      playing,
      frozen: !playing, // pause IS freeze (the game clock stops) — parity with the 3D viewer
      speed,
      gameTicks: gameTicksNow(), // the playhead clock (host ticks) — sub-update scrub oracle
      zoom,
      maxZoom,
      fogSide,
      renderer: rendererName,
      hashes: hashState,
      checkpointRestoreFails, // seeks that fell back to a from-seed rebuild (snapshot-gap signal)
      checkpoints: checkpoints.length,
      warmFrontier, // prewarm reach in updates (the scrubber's dim fill; headless drivers assert on it)
      // the timeline key moments (markers) collected so far, in timeline order
      events: [...evMap.values()].sort((x, y) => x.update - y.update),
      // the economy graph: sample count collected + whether the panel is expanded
      economy: { samples: econByUpdate.size, max: econMax, expanded: graphExpanded },
      fogOpaque: (() => {
        const fm = sim.ctx.fogm.GetMapPtr() as Uint8Array;
        let n = 0;
        for (let i = 0; i < fm.length; i++) if ((fm[i] & 0x0f) === 0) n++;
        return n;
      })(),
    }),
    pause: () => {
      playing = false;
    },
    play: () => {
      playing = true;
    },
    advance: (n: number) => {
      playing = false;
      advanceUpdates(n);
      recenter();
      present();
      return sim.sim.GetUpdateCount();
    },
    stepClock: (ms = kcmsUpdate) => {
      playing = false;
      stepGameMs += Math.max(0, ms); // consumed by the next frame()'s clock integration
      return Math.round(Math.max(0, playhead) * kcmsUpdate + stepGameMs);
    },
    seek: (u: number) => {
      seekTo(u);
      recenter();
      present();
      return sim.sim.GetUpdateCount();
    },
    zoom: (z: number) => {
      setZoom(z);
      return zoom;
    },
    fog: (s: number | null) => {
      if (s === null) {
        if (fogSide !== null) setFogSide(fogSide);
      } else setFogSide(s);
      return fogSide;
    },
    stop: () => {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(rafId);
      prewarmGen++; // halts any in-thread warmer slices
      prewarmWorker?.terminate();
      prewarmWorker = null;
      ro.disconnect();
      for (const [type, fn] of winListeners) window.removeEventListener(type, fn as never);
      audio.dispose(); // unhook the gesture-unlock listeners + close the AudioContext
      helpEl?.remove();
      helpEl = null;
      try {
        // release the GL context so repeated open/close doesn't exhaust contexts
        canvas.getContext('webgl2')?.getExtension('WEBGL_lose_context')?.loseContext();
      } catch {
        /* CPU path / already lost */
      }
      opts.mount.replaceChildren();
      opts.mount.classList.remove('wirv');
      const w = window as unknown as { wiReplay?: unknown };
      if (w.wiReplay === handle) delete w.wiReplay;
    },
  };
  (window as unknown as { wiReplay: unknown }).wiReplay = handle;
  return handle;
}
